import { useEffect, useRef, type KeyboardEvent, type ReactNode } from 'react';

import { IconClose } from './icons.tsx';

interface SheetProps {
  readonly open: boolean;
  readonly title: string;
  readonly footer?: string;
  readonly onClose: () => void;
  readonly children: ReactNode;
}

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea, input, select, iframe, [tabindex]:not([tabindex="-1"])';

export const BottomSheet = ({
  open,
  title,
  footer,
  onClose,
  children,
}: SheetProps): ReactNode => {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);

  // Escape-to-close — registered once, gated by `open`.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: globalThis.KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return (): void => {
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  // Initial focus + restore on close — without this, keyboard users
  // entering the sheet stay anchored to the (now-hidden) trigger and
  // Tab leaks them into background controls.
  useEffect(() => {
    if (open) {
      restoreFocusRef.current = (document.activeElement instanceof HTMLElement)
        ? document.activeElement
        : null;
      // Defer focus until the open transition has applied; otherwise
      // some assistive tech reads the title from the closed state.
      const t = window.setTimeout(() => closeBtnRef.current?.focus(), 50);
      return (): void => { window.clearTimeout(t); };
    }
    // Guard: the trigger can unmount (e.g. a carousel swap) while the sheet is
    // open — focusing a detached node silently drops focus to <body>.
    if (restoreFocusRef.current !== null && document.contains(restoreFocusRef.current)) {
      restoreFocusRef.current.focus();
    }
    return undefined;
  }, [open]);

  // Light focus trap — keeps Tab inside the dialog. Three focusable
  // elements at most (close + iframe + maybe link); roundtripping
  // first/last is enough without a full inert-rest-of-tree solution.
  const onDialogKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (e.key !== 'Tab') return;
    const root = dialogRef.current;
    if (root === null) return;
    const focusables = root.querySelectorAll<HTMLElement>(FOCUSABLE);
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (first === undefined || last === undefined) return;
    const active = document.activeElement;
    if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return (
    <>
      <div
        className={`sheet-backdrop${open ? ' open' : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={dialogRef}
        className={`sheet${open ? ' open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-hidden={!open}
        aria-labelledby="sheet-title"
        onKeyDown={onDialogKeyDown}
      >
        <div className="sheet-handle" aria-hidden="true" />
        <div className="sheet-header">
          <p className="sheet-title" id="sheet-title">{title}</p>
          <button
            ref={closeBtnRef}
            type="button"
            className="sheet-close"
            onClick={onClose}
            aria-label="Close"
          >
            <IconClose />
          </button>
        </div>
        <div className="sheet-iframe-wrap">{children}</div>
        {footer !== undefined ? <p className="sheet-footer">{footer}</p> : null}
      </div>
    </>
  );
};
